import { Button } from "@/components/ui/Button";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useEffect, useState, type ReactNode } from "react";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

type ListingCarouselProps<Offer extends { id: number }> = {
  offers: Offer[];
  navigationId: string;
  previousLabel: string;
  nextLabel: string;
  emptyContent: ReactNode;
  leadingSlide?: ReactNode;
  trailingSlide?: ReactNode;
  supplementaryOffers?: Offer[];
  renderOffer: (offer: Offer, index: number) => ReactNode;
  renderFooter?: (offer: Offer, index: number) => ReactNode;
  renderSupplementaryOffer?: (offer: Offer, index: number) => ReactNode;
  renderSupplementaryFooter?: (offer: Offer, index: number) => ReactNode;
  showNavigation?: boolean;
  showPagination?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
};

export function ListingCarousel<Offer extends { id: number }>({
  offers,
  navigationId,
  previousLabel,
  nextLabel,
  emptyContent,
  leadingSlide,
  trailingSlide,
  supplementaryOffers = [],
  renderOffer,
  renderFooter,
  renderSupplementaryOffer,
  renderSupplementaryFooter,
  showNavigation = true,
  showPagination = true,
  autoPlay = true,
  loop = true,
}: ListingCarouselProps<Offer>) {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const previousClass = `listing-prev-${navigationId}`;
  const nextClass = `listing-next-${navigationId}`;
  const leadingSlideOffset = leadingSlide ? 1 : 0;
  const trailingSlideOffset = trailingSlide ? 1 : 0;
  const activeOfferIndex = activeSlideIndex - leadingSlideOffset;
  const activeSupplementaryIndex = activeOfferIndex - offers.length;
  const hasOfferSlides = offers.length > 0 || supplementaryOffers.length > 0;

  useEffect(() => {
    setActiveSlideIndex((currentIndex) =>
      Math.min(
        currentIndex,
        Math.max(
          offers.length +
            supplementaryOffers.length +
            leadingSlideOffset +
            trailingSlideOffset -
            1,
          0,
        ),
      ),
    );
  }, [
    leadingSlideOffset,
    offers.length,
    supplementaryOffers.length,
    trailingSlideOffset,
  ]);

  return (
    <div className="demo-carousel-wrap">
      {hasOfferSlides ? (
        <>
          <Swiper
            grabCursor
            centeredSlides
            slidesPerView={1}
            pagination={showPagination ? { clickable: true } : false}
            loop={loop}
            autoplay={
              autoPlay ? { delay: 3000, disableOnInteraction: true } : false
            }
            navigation={
              showNavigation
                ? {
                    nextEl: `.${nextClass}`,
                    prevEl: `.${previousClass}`,
                  }
                : false
            }
            modules={[Navigation, Pagination, Autoplay]}
            className="demo-swiper"
            onSlideChange={(swiper) => setActiveSlideIndex(swiper.realIndex)}
          >
            {leadingSlide && (
              <SwiperSlide key="leading-slide">
                <div className="demo-slide">{leadingSlide}</div>
              </SwiperSlide>
            )}
            {offers.map((offer, index) => (
              <SwiperSlide key={offer.id}>
                <div className="demo-slide">{renderOffer(offer, index)}</div>
              </SwiperSlide>
            ))}
            {renderSupplementaryOffer &&
              supplementaryOffers.map((offer, index) => (
                <SwiperSlide key={`supplementary-${offer.id}`}>
                  <div className="demo-slide">
                    {renderSupplementaryOffer(offer, index)}
                  </div>
                </SwiperSlide>
              ))}
            {trailingSlide && (
              <SwiperSlide key="trailing-slide">
                <div className="demo-slide">{trailingSlide}</div>
              </SwiperSlide>
            )}
          </Swiper>

          {showNavigation && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={`${previousClass} demo-nav-btn -left-4 md:-left-8`}
                aria-label={previousLabel}
              >
                <ChevronLeftIcon className="size-5 md:w-8 md:h-8" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={`${nextClass} demo-nav-btn -right-4 md:-right-8`}
                aria-label={nextLabel}
              >
                <ChevronRightIcon className="size-5 md:w-8 md:h-8" />
              </Button>
            </>
          )}
          {renderFooter &&
            activeOfferIndex >= 0 &&
            activeOfferIndex < offers.length &&
            offers[activeOfferIndex] && (
              <div className="mt-4">
                {renderFooter(offers[activeOfferIndex], activeOfferIndex)}
              </div>
            )}
          {renderSupplementaryFooter &&
            activeSupplementaryIndex >= 0 &&
            supplementaryOffers[activeSupplementaryIndex] && (
              <div className="mt-4">
                {renderSupplementaryFooter(
                  supplementaryOffers[activeSupplementaryIndex],
                  activeSupplementaryIndex,
                )}
              </div>
            )}
        </>
      ) : (
        emptyContent
      )}
    </div>
  );
}
